import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import Footer from "../../core/common/Footer";
import useApi from "../../core/customhook/useApi";
import webservice_url from "../../core/webservice/Webservice_url";
import { all_routes } from "../router/all_routes";
import MultiSearchableSelect from "../../core/common/MultiSearchableSelect";
import CommonFilterSelect, {
  FilterTypes,
} from "../../core/common/CommonFilterSelect";
import ExcelJS from "exceljs";

const BULK_UPLOAD_URL = webservice_url.UploadDrugCandidate;
const MAX_FILE_SIZE_MB = 5;
const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];
type TabKey = "manual" | "excel";
interface Candidate {
  assigned_drug_check: any;
  hrms_id: any;
  assignment_id: string;
  candidate_name: any;
  last_name: any;
  first_name: any;
  status_id: any;
  status_name: any;
  candidate_id: string;
  name: string;
  email: string;
  case_id?: string;
}

interface DrugCategory {
  check_name: any;
  check_id: any | null | undefined;
  status_name: any;
  category_id: string;
  category_name: string;
}

interface FilterData {
  candidates: string[];
  category: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const AddDrugReport = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabKey>("manual");

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [categories, setCategories] = useState<DrugCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [filterData, setFilterData] = useState<FilterData>({
    candidates: [],
    category: "",
  });

  const DRUG_TEST_OPTIONS = [
    "Drug Test",
    "Drug Test - 4 Panel",
    "Drug Test - 5 Panel",
    "Drug Test - 8 Panel",
    "Drug Test - 9 Panel",
    "Drug Test - 10 Panel",
    "Drug Test - 12 Panel",
  ];

  const [commonFilterData, setCommonFilterData] = useState<FilterTypes | null>(
    null,
  );

  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelCategory, setExcelCategory] = useState("");
  const [excelErrors, setExcelErrors] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const getCandidatesAPI = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getCategoriesAPI = useApi<any>(webservice_url.AllCandidateDrugList, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const uploadReportAPI = useApi<any>(webservice_url.AddCandidateReport, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const DownloadExcel = useApi<any>(webservice_url.DownloadAllCandidateList, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const bulkUploadAPI = useApi<any>(BULK_UPLOAD_URL, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchCategories = async () => {
    try {
      const categoryRes = await getCategoriesAPI.callApi();
      setCategories(categoryRes?.data ?? []);
    } catch (err) {
      console.error(err);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCandidates = async () => {
    try {
      const candidateRes = await getCandidatesAPI.callApi(
        undefined,
        `${webservice_url.AllCandidateList}/${commonFilterData?.program}`,
      );
      setCandidates(candidateRes?.data ?? []);
    } catch (err) {
      console.error(err);
      setCandidates([]);
    }
  };

  useEffect(() => {
    if (commonFilterData?.program) {
      fetchCandidates();
      setFilterData((prev) => ({ ...prev, candidates: [] }));
    } else {
      setCandidates([]);
    }
  }, [commonFilterData?.program]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!filterData.candidates || filterData.candidates.length === 0) {
      newErrors.candidates = "Please select at least one candidate";
    }
    if (!selectedCategory) {
      newErrors.category = "Please select a drug test category";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const candidateOptions = candidates.map((c) => ({
    label: `${c.hrms_id} - ${c.candidate_name}${c.assigned_drug_check != null ? ` - ${c.assigned_drug_check}` : ""}`,
    value: c.candidate_id,
  }));

  const selectedCandidateDetails = candidateOptions.filter((option) =>
    filterData.candidates?.includes(option.value),
  );

  const handleUpload = async () => {
    if (!validate()) return;

    try {
      const candidateDetails = candidates
        .filter((c) => filterData.candidates.includes(c.candidate_id))
        .map((c) => ({
          assignment_id: c.assignment_id,
          candidate_id: c.candidate_id,
        }));

      const payload = {
        candidateDetails,
        check_id: selectedCategory,
      };

      const response = await uploadReportAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Report uploaded successfully",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(all_routes.drugReport);
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleSelectDataChange = (name: string, value: any) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const validateExcelFile = (file: File): string => {
    const lowerName = file.name.toLowerCase();
    const isValidType = ALLOWED_EXTENSIONS.some((ext) =>
      lowerName.endsWith(ext),
    );
    if (!isValidType) {
      return "Only Excel files (.xlsx or .xls) are allowed";
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size must be ${MAX_FILE_SIZE_MB} MB or less`;
    }
    return "";
  };

  const handleFileSelected = (file: File | null | undefined) => {
    if (!file) return;
    const error = validateExcelFile(file);
    if (error) {
      setExcelFile(null);
      setExcelErrors((prev) => ({ ...prev, file: error }));
      return;
    }
    setExcelFile(file);
    setExcelErrors((prev) => ({ ...prev, file: "" }));
  };

  const handleRemoveFile = () => {
    setExcelFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelected(e.dataTransfer.files?.[0]);
  };

  const validateExcelForm = () => {
    const newErrors: Record<string, string> = {};
    if (!excelFile) {
      newErrors.file = "Please upload the filled Excel file";
    }
    setExcelErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBulkUpload = async () => {
    if (!validateExcelForm() || !excelFile) return;

    try {
      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("check_id", excelCategory);

      const response = await bulkUploadAPI.callApi(formData);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Bulk report uploaded successfully",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(all_routes.drugReport);
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: response?.message || "Could not process the Excel file",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  }; 

  const handleDownloadExcel = async () => {
    try {
      const payload: Record<string, any> = {};

      if (excelCategory) {
        payload.check_id = excelCategory;
      }

      const res = await DownloadExcel.callApi(payload);
      const list: any[] = res?.data ?? [];

      if (!list.length) {
        Swal.fire({
          icon: "info",
          title: "No candidates found",
          text: "There are no pending candidates to download.",
        });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Pending Candidates");
 
      sheet.columns = [
        { header: "Sr. No", key: "sno", width: 8 },
        { header: "Candidate ID", key: "candidate_id", width: 38 },
        { header: "Candidate Name", key: "candidate_name", width: 28 },
        { header: "Check Name", key: "assigned_drug_check", width: 26 }, 
        { header: "Account Name (optional)", key: "program_name", width: 20 }, 
      ]; 

      list.forEach((c: any, index: number) => {
        sheet.addRow({
          sno: index + 1,
          candidate_id: c.candidate_id ?? "",
          candidate_name: c.candidate_name ?? "",
          assigned_drug_check: c.assigned_drug_check ?? "", 
          program_name: c.program_name ?? "", 
        });
      });
 
      sheet.getRow(1).eachCell((cell) => {
        cell.font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF0000" },
        };

        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };
      });
 
      const CHECK_COL = 4;

      for (let row = 2; row <= list.length + 1; row++) {
        sheet.getCell(row, CHECK_COL).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${DRUG_TEST_OPTIONS.join(",")}"`],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid selection",
          error: "Please choose a value from the dropdown list.",
        };
      }
 
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const dateStamp = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);
      link.download = `pending_drug_report_candidates_${dateStamp}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Could not download the Excel file",
      });
    }
  };

  const isPackageSelected = Boolean(commonFilterData?.program);
  const categoriesLoading = getCategoriesAPI.loading;
  const renderTabs = () => (
    <div className="px-4 pt-3 border-bottom">
      <ul className="nav nav-tabs border-0" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "manual"}
            className={`nav-link d-flex align-items-center gap-2 fw-semibold ${
              activeTab === "manual" ? "active" : ""
            }`}
            onClick={() => setActiveTab("manual")}
          >
            <i className="ti ti-user-check"></i>
            Select Candidates
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "excel"}
            className={`nav-link d-flex align-items-center gap-2 fw-semibold ${
              activeTab === "excel" ? "active" : ""
            }`}
            onClick={() => setActiveTab("excel")}
          >
            <i className="ti ti-file-spreadsheet"></i>
            Upload Excel
          </button>
        </li>
      </ul>
    </div>
  );

  const renderManualTab = () => (
    <> 
      <div className="px-4 pt-4">
        <div className="border rounded-3 p-3 bg-light">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                width: "34px",
                height: "34px",
                background: "#e6f0ff",
              }}
            >
              <i
                className="ti ti-filter"
                style={{ fontSize: "16px", color: "#2f6feb" }}
              ></i>
            </div>
            <h6 className="fw-semibold mb-0">Filter Candidates</h6>
          </div>

          <CommonFilterSelect
            colClass="col-12 col-sm-6 col-lg-3"
            hideDepartmentAndPackage={true}
            handleGetFilters={(data) => {
              setCommonFilterData(data);
            }}
          />
        </div>
      </div>
 
      <div className="card-body p-4">
        {!isPackageSelected ? (
          <div className="text-center py-5">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
              style={{
                width: "70px",
                height: "70px",
                background: "#fdeee0",
              }}
            >
              <i
                className="ti ti-filter-cog"
                style={{ fontSize: "32px", color: "#e08b2f" }}
              ></i>
            </div>
            <h6 className="fw-semibold mb-1">Complete Filters to Continue</h6>
            <p
              className="text-muted mb-0"
              style={{ maxWidth: "420px", margin: "0 auto" }}
            >
              Please select Organization, Account, Location and Program above to load matching candidates.
            </p>
          </div>
        ) : (
          <>
            <div className="row g-4"> 
              <div className="col-lg-7">
                <div className="border rounded-3 h-100 d-flex flex-column overflow-hidden">
                  <div className="p-3 border-bottom bg-light">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{
                            width: "30px",
                            height: "30px",
                            background: "#e8ecfb",
                          }}
                        >
                          <i
                            className="ti ti-users"
                            style={{ fontSize: "15px", color: "#4c5fd5" }}
                          ></i>
                        </div>
                        Select Candidates
                      </h6>
                      <span className="badge bg-primary-subtle text-primary fw-semibold px-3 py-2 rounded-pill">
                        {filterData.candidates.length} Selected
                      </span>
                    </div>

                    <MultiSearchableSelect
                      label="Select Candidates"
                      name="candidates"
                      value={filterData?.candidates ?? []}
                      options={candidateOptions ?? []}
                      onChange={handleSelectDataChange}
                      error={errors?.candidates}
                      loading={getCandidatesAPI.loading}
                      required
                    />
                  </div>

                  <div
                    className="flex-grow-1 p-3"
                    style={{ maxHeight: "380px", overflowY: "auto" }}
                  >
                    {getCandidatesAPI.loading ? (
                      <div className="text-center text-muted py-5">
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading candidates...
                      </div>
                    ) : selectedCandidateDetails.length > 0 ? (
                      <div className="row g-2">
                        {selectedCandidateDetails.map((candidate) => (
                          <div className="col-md-6" key={candidate.value}>
                            <div className="d-flex align-items-center gap-3 p-2 rounded-3 border bg-light h-100">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  background: "#e8ecfb",
                                }}
                              >
                                <i
                                  className="ti ti-user"
                                  style={{
                                    fontSize: "18px",
                                    color: "#4c5fd5",
                                  }}
                                ></i>
                              </div>
                              <div className="flex-grow-1 text-truncate">
                                <div className="fw-medium text-truncate">
                                  {candidate.label}
                                </div>
                              </div>
                              <i className="ti ti-circle-check-filled text-primary fs-5"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : candidateOptions.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <i className="ti ti-user-off fs-1 d-block mb-2"></i>
                        No candidates found for the selected filters
                      </div>
                    ) : (
                      <div className="text-center text-muted py-5">
                        <i className="ti ti-users-group fs-1 d-block mb-2"></i>
                        No candidates selected
                      </div>
                    )}
                  </div>

                  {errors.candidates && (
                    <div className="text-danger small px-3 py-2 border-top">
                      <i className="ti ti-alert-circle me-1"></i>
                      {errors.candidates}
                    </div>
                  )}
                </div>
              </div>
 
              <div className="col-lg-5">
                <div className="d-flex flex-column gap-4 h-100">
                  <div className="border rounded-3 p-3">
                    <label className="form-label fw-semibold d-flex align-items-center gap-2">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                          width: "30px",
                          height: "30px",
                          background: "#f2e8fb",
                        }}
                      >
                        <i
                          className="ti ti-flask"
                          style={{ fontSize: "15px", color: "#9b51e0" }}
                        ></i>
                      </div>
                      Drug Test Category
                      <span className="text-danger ms-1">*</span>
                    </label>

                    {categoriesLoading ? (
                      <div className="text-muted small py-2">
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading categories...
                      </div>
                    ) : (
                      <>
                        <select
                          className={`form-select ${
                            errors.category ? "is-invalid" : ""
                          }`}
                          value={selectedCategory}
                          onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              category: "",
                            }));
                          }}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.check_id} value={cat.check_id}>
                              {cat.check_name}
                            </option>
                          ))}
                        </select>
                        {errors.category && (
                          <div className="invalid-feedback d-block">
                            {errors.category}
                          </div>
                        )}
                      </>
                    )}
                  </div>
 
                  <div className="border rounded-3 p-3 bg-light flex-grow-1">
                    <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                      <i className="ti ti-clipboard-list text-primary"></i>
                      Summary
                    </h6>
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <span className="text-muted small">
                        Candidates Selected
                      </span>
                      <span className="fw-semibold">
                        {filterData.candidates.length}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Category</span>
                      <span
                        className="fw-semibold text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {categories.find(
                          (c) =>
                            String(c.check_id) === String(selectedCategory),
                        )?.check_name || "Not selected"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
 
            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
              <button
                type="button"
                className="btn btn-primary px-4 d-flex align-items-center gap-2"
                onClick={handleUpload}
                disabled={uploadReportAPI.loading}
              >
                {uploadReportAPI.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-upload"></i>
                    Upload Report
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  const renderExcelTab = () => (
    <div className="card-body p-4">
      <div className="row g-4">
        <div className="col-lg-12">
          <div className="d-flex flex-column gap-3">
            <div className="border rounded-3 p-3">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-semibold"
                  style={{
                    width: "34px",
                    height: "34px",
                    background: "#e6f0ff",
                    color: "#2f6feb",
                  }}
                >
                  1
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-semibold mb-1">
                    Download the pending candidates file
                  </h6>
                  <p className="text-muted small mb-2">
                    This file lists only the candidates who don't have a drug
                    report yet. Candidates who already have a report are not
                    included.
                  </p>
                  <p className="text-muted small mb-3">
                    Tip: select the drug test category on the right first, so
                    the list is based on that category. Do not rename or reorder
                    the column headers.
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadExcel}
                    disabled={DownloadExcel.loading}
                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                  >
                    {DownloadExcel.loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Preparing file...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-download"></i>
                        Download pending candidates
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
 
            <div className="border rounded-3 p-3">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-semibold"
                  style={{
                    width: "34px",
                    height: "34px",
                    background: "#fdeee0",
                    color: "#e08b2f",
                  }}
                >
                  2
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-semibold mb-1">
                    Fill in the report details
                  </h6>
                  <p className="text-muted small mb-0">
                    Fill the remaining columns for each candidate in the
                    downloaded file. You can delete rows for candidates you
                    don't want to upload. Save the file and keep it under{" "}
                    {MAX_FILE_SIZE_MB} MB (.xlsx or .xls only).
                  </p>
                </div>
              </div>
            </div>
 
            <div className="border rounded-3 p-3">
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-semibold"
                  style={{
                    width: "34px",
                    height: "34px",
                    background: "#e6f7ee",
                    color: "#1e9e5a",
                  }}
                >
                  3
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-semibold mb-1">Upload the filled file</h6>
                  <p className="text-muted small mb-3">
                    Drag the file into the box or click to browse.
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="d-none"
                    onChange={(e) => handleFileSelected(e.target.files?.[0])}
                  />

                  {!excelFile ? (
                    <div
                      role="button"
                      tabIndex={0}
                      className="text-center rounded-3 p-4"
                      style={{
                        border: `2px dashed ${
                          excelErrors.file
                            ? "#dc3545"
                            : isDragging
                              ? "#2f6feb"
                              : "#c4cad4"
                        }`,
                        background: isDragging ? "#f0f6ff" : "#fafbfc",
                        cursor: "pointer",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                    >
                      <i className="ti ti-cloud-upload fs-1 text-primary d-block mb-2"></i>
                      <div className="fw-medium">
                        Drop your Excel file here, or{" "}
                        <span className="text-primary">browse</span>
                      </div>
                      <div className="text-muted small mt-1">
                        .xlsx or .xls, up to {MAX_FILE_SIZE_MB} MB
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-3 p-3 rounded-3 border bg-light">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                          width: "42px",
                          height: "42px",
                          background: "#e6f7ee",
                        }}
                      >
                        <i
                          className="ti ti-file-spreadsheet"
                          style={{ fontSize: "20px", color: "#1e9e5a" }}
                        ></i>
                      </div>
                      <div className="flex-grow-1 text-truncate">
                        <div className="fw-medium text-truncate">
                          {excelFile.name}
                        </div>
                        <div className="text-muted small">
                          {formatFileSize(excelFile.size)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-light border"
                        onClick={handleRemoveFile}
                        disabled={bulkUploadAPI.loading}
                        aria-label="Remove file"
                      >
                        <i className="ti ti-trash text-danger"></i>
                      </button>
                    </div>
                  )}

                  {excelErrors.file && (
                    <div className="text-danger small mt-2">
                      <i className="ti ti-alert-circle me-1"></i>
                      {excelErrors.file}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-primary px-4 d-flex align-items-center gap-2"
          onClick={handleBulkUpload}
          disabled={bulkUploadAPI.loading}
        >
          {bulkUploadAPI.loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Uploading...
            </>
          ) : (
            <>
              <i className="ti ti-upload"></i>
              Upload Excel
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content p-2 p-md-3">
        <div className="card border-0 shadow-sm">
          {/* Header */}
          <div className="card-header bg-white d-flex align-items-center justify-content-between py-3">
            <div>
              <h5 className="fw-semibold mb-0">Add Bulk Drug Report</h5>
              <small className="text-muted">
                Pick candidates manually, or upload an Excel file to add many at
                once
              </small>
            </div>
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => navigate(all_routes.drugReport)}
            >
              <i className="ti ti-arrow-left me-1"></i>
              Back
            </button>
          </div>

          {/* Tabs */}
          {renderTabs()}

          {/* Tab content */}
          {activeTab === "manual" ? renderManualTab() : renderExcelTab()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddDrugReport;
